
import React from 'react';
import { format } from 'date-fns';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Info, Send } from 'lucide-react';
import { Subscription } from '@/types/subscription';

interface SubscriptionsTableProps {
  subscriptions: Subscription[];
  onViewOnboarding: (subscription: Subscription) => void;
  onSendTrialReminder: (subscription: Subscription) => void;
}

const SubscriptionsTable: React.FC<SubscriptionsTableProps> = ({
  subscriptions,
  onViewOnboarding,
  onSendTrialReminder
}) => {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Business</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>Start Date</TableHead>
            <TableHead>End Date</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>First Login</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {subscriptions.map((subscription) => (
            <TableRow key={subscription.id}>
              <TableCell className="font-medium">{subscription.businessName}</TableCell>
              <TableCell>{subscription.plan}</TableCell>
              <TableCell>{format(subscription.startDate, 'MMM d, yyyy')}</TableCell>
              <TableCell>
                {subscription.status === 'trial' && subscription.trialEndDate
                  ? `${format(subscription.trialEndDate, 'MMM d, yyyy')} (Trial)`
                  : format(subscription.endDate, 'MMM d, yyyy')
                }
              </TableCell>
              <TableCell>
                {subscription.status === 'trial' ? 'Free' : `$${subscription.amount.toFixed(2)}`}
              </TableCell>
              <TableCell>
                <Badge 
                  variant={
                    subscription.status === 'active' ? 'default' :
                    subscription.status === 'trial' ? 'outline' :
                    'destructive'
                  }
                >
                  {subscription.status}
                </Badge>
              </TableCell>
              <TableCell>
                {subscription.firstLoginDate 
                  ? format(subscription.firstLoginDate, 'MMM d, yyyy')
                  : 'Not logged in yet'
                }
              </TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" onClick={() => onViewOnboarding(subscription)}>
                    <Info className="h-4 w-4" />
                  </Button>
                  {subscription.status === 'trial' && (
                    <Button 
                      variant={subscription.trialReminderSent ? 'slide' : 'outline'} 
                      size="sm"
                      onClick={() => onSendTrialReminder(subscription)}
                      disabled={subscription.trialReminderSent}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {subscription.trialReminderSent ? 'Reminder Sent' : 'Send Reminder'}
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default SubscriptionsTable;
